/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { withLatestFrom, distinctUntilChanged, map, take, switchMap } from 'rxjs/operators';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import PreferencesStore from './preference-store.service';
import { AppState } from 'src/app/services/app-state.service';
import { PreferencesManagerBase } from 'src/app/app-data/interfaces/preferences-manager-base.interface';
import { SortTicketListOptions } from '../modules/plan-tickets-filter/components/filtered-ticket-list-panel/filtered-ticket-list-panel.component';

import firebase from 'firebase/compat/app';

export interface IUserPreferences {
	autoOpenProjectOverview: boolean;
	canvasTextOptions: string;
	canvasTextScale: number;
	crewSizesEnabled: boolean;
	disableWebGL: boolean;
	enableDependencyText: boolean;
	enableSolidLines: boolean;
	enableTicketOverlap: boolean;
	forceCanvasRenderer: boolean;
	forceFXAA: boolean;
	hideCompletedTickets: boolean;
	planDisableAliasing: boolean;
	sharedMeeting: boolean;
	showAllDependencies: boolean;
	showCustomFieldsHiddenTickets: boolean;
	showLaborCount: boolean;
	showPinHistory: boolean;
	planFilterSortBy: SortTicketListOptions;
	planFilterTransparency: number;
	useModernTimeline: boolean;
}
const DEFAULT_USER_PREFERENCES: IUserPreferences = {
	autoOpenProjectOverview: false,
	canvasTextOptions: 'default',
	canvasTextScale: 1,
	crewSizesEnabled: false,
	disableWebGL: false,
	enableDependencyText: false,
	enableSolidLines: false,
	enableTicketOverlap: false,
	forceCanvasRenderer: false,
	forceFXAA: false,
	hideCompletedTickets: false,
	planDisableAliasing: false,
	sharedMeeting: false,
	showAllDependencies: false,
	showCustomFieldsHiddenTickets: false,
	showLaborCount: false,
	showPinHistory: false,
	planFilterSortBy: SortTicketListOptions.Ascending,
	planFilterTransparency: 0,
	useModernTimeline: false,
};

// This interface implements the setter and getter for all preferences but also use the common manager for preferences
export interface IUserPreferencesManager extends IUserPreferences, PreferencesManagerBase<IUserPreferences> {}

@Injectable({
	providedIn: 'root',
})
export class UserPreferenceStateService
	extends PreferencesStore<IUserPreferences>
	implements IUserPreferencesManager
{
	constructor(private fireDB: AngularFireDatabase, private appState: AppState) {
		super(DEFAULT_USER_PREFERENCES, { storagePrefix: 'userPreferences' });
		this.firebaseUserPreferences$.pipe(take(1)).subscribe((userPreferences) => {
			this.setPreferences(userPreferences);
		});
		// This side effect will react to preferences state changes to store the data in Firebase
		this.listenPreferencesChanges(this.preferences$);
	}

	/**
	 * Selectors for each preference
	 * Remember to add a selector for each preference new preference added
	 */
	private _autoOpenProjectOverview$ = this.select((state) => state.autoOpenProjectOverview);
	private _canvasTextOptions$ = this.select((state) => state.canvasTextOptions);
	private _canvasTextScale$ = this.select((state) => state.canvasTextScale);
	private _crewSizesEnabled$ = this.select((state) => state.crewSizesEnabled);
	private _disableWebGL$ = this.select((state) => state.disableWebGL);
	private _enableDependencyText$ = this.select((state) => state.enableDependencyText);
	private _enableSolidLines$ = this.select((state) => state.enableSolidLines);
	private _enableTicketOverlap$ = this.select((state) => state.enableTicketOverlap);
	private _forceCanvasRenderer$ = this.select((state) => state.forceCanvasRenderer);
	private _forceFXAA$ = this.select((state) => state.forceFXAA);
	private _hideCompletedTickets$ = this.select((state) => state.hideCompletedTickets);
	private _planDisableAliasing$ = this.select((state) => state.planDisableAliasing);
	private _sharedMeeting$ = this.select((state) => state.sharedMeeting);
	private _showAllDependencies$ = this.select((state) => state.showAllDependencies);
	private _showCustomFieldsHiddenTickets$ = this.select((state) => state.showCustomFieldsHiddenTickets);
	private _showLaborCount$ = this.select((state) => state.showLaborCount);
	private _showPinHistory$ = this.select((state) => state.showPinHistory);
	private _planFilterSortBy$ = this.select((state) => state.planFilterSortBy);
	private _planFilterTransparency$ = this.select((state) => state.planFilterTransparency);
	private _useModernTimeline$ = this.select((state) => state.useModernTimeline);

	private userPreferencesFirebaseObject$ = this.appState.userId.pipe(
		distinctUntilChanged(),
		map((userId) => this.getUserPreferencesFirebaseObject(userId))
	);

	private firebaseUserPreferences$ = this.userPreferencesFirebaseObject$.pipe(
		switchMap((userPreferencesFirebaseObject) => userPreferencesFirebaseObject.valueChanges())
	);

	/**
	 * This method will store the preferences in the DB each time the preferences were updated
	 */
	private listenPreferencesChanges = this.effect<IUserPreferences>((preferences$) => {
		return preferences$.pipe(
			withLatestFrom(this.userPreferencesFirebaseObject$),
			tapResponse(
				([preferences, userPreferencesFirebaseObject]) => {
					if (userPreferencesFirebaseObject) {
						userPreferencesFirebaseObject.update(preferences);
					}
				},
				(err) => {
					console.error(err);
				}
			)
		);
	});

	// Setters and getters of each preference
	// hideCompletedTickets
	set hideCompletedTickets(value: boolean) {
		this.setPreference('hideCompletedTickets', value);
	}
	set hideCompletedTickets$(value: Observable<boolean>) {
		this.setPreference('hideCompletedTickets', value);
	}
	get hideCompletedTickets(): boolean {
		return this.getPreference('hideCompletedTickets') as boolean;
	}
	get hideCompletedTickets$(): Observable<boolean> {
		return this._hideCompletedTickets$;
	}

	// showPinHistory
	set showPinHistory(value: boolean) {
		this.setPreference('showPinHistory', value);
	}
	set showPinHistory$(value: Observable<boolean>) {
		this.setPreference('showPinHistory', value);
	}
	get showPinHistory(): boolean {
		return this.getPreference('showPinHistory') as boolean;
	}
	get showPinHistory$(): Observable<boolean> {
		return this._showPinHistory$;
	}

	// showAllDependencies
	set showAllDependencies(value: boolean) {
		this.setPreference('showAllDependencies', value);
	}
	set showAllDependencies$(value: Observable<boolean>) {
		this.setPreference('showAllDependencies', value);
	}
	get showAllDependencies(): boolean {
		return this.getPreference('showAllDependencies') as boolean;
	}
	get showAllDependencies$(): Observable<boolean> {
		return this._showAllDependencies$;
	}

	// enableTicketOverlap
	set enableTicketOverlap(value: boolean) {
		this.setPreference('enableTicketOverlap', value);
	}
	set enableTicketOverlap$(value: Observable<boolean>) {
		this.setPreference('enableTicketOverlap', value);
	}
	get enableTicketOverlap(): boolean {
		return this.getPreference('enableTicketOverlap') as boolean;
	}
	get enableTicketOverlap$(): Observable<boolean> {
		return this._enableTicketOverlap$;
	}

	// enableSolidLines
	set enableSolidLines(value: boolean) {
		this.setPreference('enableSolidLines', value);
	}
	set enableSolidLines$(value: Observable<boolean>) {
		this.setPreference('enableSolidLines', value);
	}
	get enableSolidLines(): boolean {
		return this.getPreference('enableSolidLines') as boolean;
	}
	get enableSolidLines$(): Observable<boolean> {
		return this._enableSolidLines$;
	}

	// showLaborCount
	set showLaborCount(value: boolean) {
		this.setPreference('showLaborCount', value);
	}
	set showLaborCount$(value: Observable<boolean>) {
		this.setPreference('showLaborCount', value);
	}
	get showLaborCount(): boolean {
		return this.getPreference('showLaborCount') as boolean;
	}
	get showLaborCount$(): Observable<boolean> {
		return this._showLaborCount$;
	}

	// crewSizesEnabled
	set crewSizesEnabled(value: boolean) {
		this.setPreference('crewSizesEnabled', value);
	}
	set crewSizesEnabled$(value: Observable<boolean>) {
		this.setPreference('crewSizesEnabled', value);
	}
	get crewSizesEnabled(): boolean {
		return this.getPreference('crewSizesEnabled') as boolean;
	}
	get crewSizesEnabled$(): Observable<boolean> {
		return this._crewSizesEnabled$;
	}

	// sharedMeeting
	set sharedMeeting(value: boolean) {
		this.setPreference('sharedMeeting', value);
	}
	set sharedMeeting$(value: Observable<boolean>) {
		this.setPreference('sharedMeeting', value);
	}
	get sharedMeeting(): boolean {
		return this.getPreference('sharedMeeting') as boolean;
	}
	get sharedMeeting$(): Observable<boolean> {
		return this._sharedMeeting$;
	}

	// enableDependencyText
	set enableDependencyText(value: boolean) {
		this.setPreference('enableDependencyText', value);
	}
	set enableDependencyText$(value: Observable<boolean>) {
		this.setPreference('enableDependencyText', value);
	}
	get enableDependencyText(): boolean {
		return this.getPreference('enableDependencyText') as boolean;
	}
	get enableDependencyText$(): Observable<boolean> {
		return this._enableDependencyText$;
	}

	// useModernTimeline
	set useModernTimeline(value: boolean) {
		this.setPreference('useModernTimeline', value);
	}
	set useModernTimeline$(value: Observable<boolean>) {
		this.setPreference('useModernTimeline', value);
	}
	get useModernTimeline(): boolean {
		return this.getPreference('useModernTimeline') as boolean;
	}
	get useModernTimeline$(): Observable<boolean> {
		return this._useModernTimeline$;
	}

	// autoOpenProjectOverview
	set autoOpenProjectOverview(value: boolean) {
		this.setPreference('autoOpenProjectOverview', value);
	}
	set autoOpenProjectOverview$(value: Observable<boolean>) {
		this.setPreference('autoOpenProjectOverview', value);
	}
	get autoOpenProjectOverview(): boolean {
		return this.getPreference('autoOpenProjectOverview') as boolean;
	}
	get autoOpenProjectOverview$(): Observable<boolean> {
		return this._autoOpenProjectOverview$;
	}

	// forceCanvasRenderer
	set forceCanvasRenderer(value: boolean) {
		this.setPreference('forceCanvasRenderer', value);
	}
	set forceCanvasRenderer$(value: Observable<boolean>) {
		this.setPreference('forceCanvasRenderer', value);
	}
	get forceCanvasRenderer(): boolean {
		return this.getPreference('forceCanvasRenderer') as boolean;
	}
	get forceCanvasRenderer$(): Observable<boolean> {
		return this._forceCanvasRenderer$;
	}

	// forceCanvasRenderer
	set canvasTextOptions(value: string) {
		this.setPreference('canvasTextOptions', value);
	}
	set canvasTextOptions$(value: Observable<string>) {
		this.setPreference('canvasTextOptions', value);
	}
	get canvasTextOptions(): string {
		return this.getPreference('canvasTextOptions') as string;
	}
	get canvasTextOptions$(): Observable<string> {
		return this._canvasTextOptions$;
	}

	// planDisableAliasing
	set planDisableAliasing(value: boolean) {
		this.setPreference('planDisableAliasing', value);
	}
	set planDisableAliasing$(value: Observable<boolean>) {
		this.setPreference('planDisableAliasing', value);
	}
	get planDisableAliasing(): boolean {
		return this.getPreference('planDisableAliasing') as boolean;
	}
	get planDisableAliasing$(): Observable<boolean> {
		return this._planDisableAliasing$;
	}

	// forceFXAA
	set forceFXAA(value: boolean) {
		this.setPreference('forceFXAA', value);
	}
	set forceFXAA$(value: Observable<boolean>) {
		this.setPreference('forceFXAA', value);
	}
	get forceFXAA(): boolean {
		return this.getPreference('forceFXAA') as boolean;
	}
	get forceFXAA$(): Observable<boolean> {
		return this._forceFXAA$;
	}

	// showCustomFieldsHiddenTickets
	set showCustomFieldsHiddenTickets(value: boolean) {
		this.setPreference('showCustomFieldsHiddenTickets', value);
	}
	set showCustomFieldsHiddenTickets$(value: Observable<boolean>) {
		this.setPreference('showCustomFieldsHiddenTickets', value);
	}
	get showCustomFieldsHiddenTickets(): boolean {
		return this.getPreference('showCustomFieldsHiddenTickets') as boolean;
	}
	get showCustomFieldsHiddenTickets$(): Observable<boolean> {
		return this._showCustomFieldsHiddenTickets$;
	}

	// canvasTextScale
	set canvasTextScale(value: number) {
		this.setPreference('canvasTextScale', value);
	}
	set canvasTextScale$(value: Observable<number>) {
		this.setPreference('canvasTextScale', value);
	}
	get canvasTextScale(): number {
		return this.getPreference('canvasTextScale') as number;
	}
	get canvasTextScale$(): Observable<number> {
		return this._canvasTextScale$;
	}

	// disableWebGL
	set disableWebGL(value: boolean) {
		this.setPreference('disableWebGL', value);
	}
	set disableWebGL$(value: Observable<boolean>) {
		this.setPreference('disableWebGL', value);
	}
	get disableWebGL(): boolean {
		return this.getPreference('disableWebGL') as boolean;
	}
	get disableWebGL$(): Observable<boolean> {
		return this._disableWebGL$;
	}

	// Plan Filter Options
	// planFilterSortBy
	get planFilterSortBy$(): Observable<SortTicketListOptions> {
		return this._planFilterSortBy$;
	}

	set planFilterSortBy$(value: Observable<SortTicketListOptions>) {
		this.setPreference('planFilterSortBy', value);
	}

	set planFilterSortBy(value: SortTicketListOptions) {
		this.setPreference('planFilterSortBy', value);
	}

	get planFilterSortBy(): SortTicketListOptions {
		return this.getPreference('planFilterSortBy') as SortTicketListOptions;
	}

	// planFilterTransparency
	get planFilterTransparency$(): Observable<number> {
		return this._planFilterTransparency$;
	}

	set planFilterTransparency$(value: Observable<number>) {
		this.setPreference('planFilterTransparency', value);
	}

	set planFilterTransparency(value: number) {
		this.setPreference('planFilterTransparency', value);
	}

	get planFilterTransparency(): number {
		return this.getPreference('planFilterTransparency') as number;
	}

	private getUserPreferencesFirebaseObject(userId: string): AngularFireObject<IUserPreferences> {
		const userPreferencesPath = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			fancyFirebaseGetters.getUserPreferencesPath(userId)
		);
		return this.fireDB.object<IUserPreferences>(userPreferencesPath);
	}
}
